import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Avatar,
  Stack,
  Paper,
  Alert,
  TextField,
  MenuItem,
  Select,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { getAbsenceTypeColor, calculateDuration, formatDate } from '../leaves/helpers';
import { updateAbsence, getAbsenceDetails } from '../../services/absenceAPIs';
import { getEmployeeImage } from '../../services/HRstaff';
import { formatPositionsDisplay } from '../../services/positionAPIs';

const absenceTy_pes = [
  { value: 'SICKNESS', label: 'Sickness' },
  { value: 'LATENESS', label: 'Lateness' },
  { value: 'PERSONAL', label: 'Personal Commitment' },
  { value: 'SUSPENSION', label: 'Suspension' },
  { value: 'DAY_OFF', label: 'Day Off' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

const AbsenceTypeLabels = {
  LATENESS: 'Lateness',
  PARTIAL_DAY: 'Partial Day',
  FULL_DAY: 'Full Day',
  MULTIPLE_DAYS: 'Multiple Days',
};

const AbsenceDetailsModal = ({ selectedAbsence, setSelectedAbsence, fetchAbsences, IsEmployee }) => {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [absenceDetails, setAbsenceDetails] = useState(null);

  const absence = absenceDetails || selectedAbsence;
  const fileUrl = absence?.justificationFile;

  const ReasonTypeLabels = {
    SICKNESS: 'Sickness',
    LATENESS: 'Lateness',
    EARLY_DEPARTURE: 'Early Departure',
    EMERGENCY: 'Emergency',
    APPOINTMENT: 'Appointment',
    PERSONAL: 'Personal Commitment',
    SUSPENSION: 'Suspension',
    DAY_OFF: 'Day Off',
    UNKNOWN: 'Unknown',
  };

  const getReasonTypeColor = (ty_pe) => {
    switch (ty_pe) {
      case 'SICKNESS': return 'error';
      case 'LATENESS': return 'warning';
      case 'PERSONAL': return 'info';
      case 'EMERGENCY': return 'secondary';
      case 'APPOINTMENT': return 'success';
      case 'DAY_OFF': return 'primary';
      default: return 'default';
    }
  };

  // Format time for display
  const formatTimeForDisplay = (timeString) => {
    if (!timeString) return 'Not specified';
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`; // Display as HH:mm
    } catch (error) {
      return timeString;
    }
  };

  // Format time for input (HH:mm format)
  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    } catch (error) {
      return timeString;
    }
  };

  // Fetch full absence details when modal opens
  useEffect(() => {
    if (selectedAbsence?.id) {
      setAbsenceDetails(null);
      setFormData({});
      setIsEditMode(false);
      setSuccessMessage('');
      setErrorMessage('');
      getAbsenceDetails(selectedAbsence.id)
        .then(setAbsenceDetails)
        .catch((err) => console.error('Failed to fetch absence details:', err));
    } else {
      setAbsenceDetails(null);
    }
  }, [selectedAbsence?.id]);

  useEffect(() => {
    if (absence) {
      let minutes = 0;
      if (absence.type === 'LATENESS' || absence.type === 'PARTIAL_DAY') {
        minutes = absence.hours ? Math.round(absence.hours * 60) : 0;
      }
      setFormData({
        ...absence,
        minutes,
        startTime: formatTimeForInput(absence.startTime),
        endTime: formatTimeForInput(absence.endTime),
      });
    }
  }, [absence]);

  // Fetch employee image
  useEffect(() => {
    if (absence?.employee?.employeeId && absence?.employee?.image) {
      const fetchImage = async () => {
        try {
          const imageUrl = await getEmployeeImage(absence.employee.employeeId);
          setProfileImageUrl(imageUrl);
        } catch (err) {
          console.error('Failed to load employee image:', err);
          setProfileImageUrl(null);
        }
      };
      fetchImage();
    } else {
      setProfileImageUrl(null);
    }

    return () => {
      if (profileImageUrl) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  }, [absence?.employee?.employeeId, absence?.employee?.image]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (profileImageUrl) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  }, [profileImageUrl]);

  const handleUpdate = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const { employee, ...absenceFields } = formData;
      const payload = {
        ...absenceFields,
        employee: { employeeId: employee?.employeeId },
        type: formData.type,
        ty_pe: formData.ty_pe,
        hours: (formData.ty_pe === 'LATENESS')
          ? formData.minutes / 60
          : formData.hours,
        startTime: formData.startTime ? `${formData.startTime}:00` : null,
        endTime: formData.endTime ? `${formData.endTime}:00` : null,
      };

      if (!payload.startTime) delete payload.startTime;
      if (!payload.endTime) delete payload.endTime;

      const updated = await updateAbsence(absence.id, payload);
      setSelectedAbsence(updated);
      setSuccessMessage('Absence updated successfully');
      fetchAbsences();
      setIsEditMode(false);
    } catch (error) {
      console.error('Update failed:', error);
      if (error?.response?.data?.message === "Hours must be provided for lateness or partial day") {
        setErrorMessage('Number of hours must be provided for lateness or partial day');
      } else {
        setErrorMessage(error?.response?.data?.message || error.message || 'Failed to update absence');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!loading) {
      setSelectedAbsence(null);
    }
  };

  // Calculate hours from start and end times
  const calculateHoursFromTimes = () => {
    if (formData.startTime && formData.endTime) {
      const [startHours, startMinutes] = formData.startTime.split(':').map(Number);
      const [endHours, endMinutes] = formData.endTime.split(':').map(Number);

      const startTotalMinutes = startHours * 60 + startMinutes;
      const endTotalMinutes = endHours * 60 + endMinutes;

      if (endTotalMinutes > startTotalMinutes) {
        const diffMinutes = endTotalMinutes - startTotalMinutes;
        const diffHours = (diffMinutes / 60).toFixed(2);
        setFormData(prev => ({ ...prev, hours: parseFloat(diffHours) }));
      } else {
        setErrorMessage('End time must be after start time');
      }
    }
  };

  if (!absence) return null;

  return (
    <Dialog
      open={Boolean(selectedAbsence)}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && !loading) {
          handleDialogClose();
        }
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h2" fontWeight="600">
            Absence Details
          </Typography>
          <IconButton onClick={handleDialogClose} disabled={loading} sx={{ color: 'grey.500' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: 3,
          py: 2,
          maxHeight: '70vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {/* Employee Information Card */}
        <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar
                src={profileImageUrl || absence.employee?.image || undefined}
                sx={{ bgcolor: (profileImageUrl || absence.employee?.image) ? 'transparent' : 'primary.main', mr: 2 }}
              >
                {!(profileImageUrl || absence.employee?.image) && <PersonIcon />}
              </Avatar>
              <Typography variant="h6" fontWeight="600">
                Employee Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Full Name
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {absence.employee?.firstName} {absence.employee?.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Department
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {(typeof absence.employee?.department === 'object' ? absence.employee?.department?.name : absence.employee?.department) || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Position
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {formatPositionsDisplay(absence.employee) || (absence.employee?.position || 'N/A')}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Absence Details & Justification File Grid */}
        <Grid container spacing={3} sx={{ width: '100%', m: 0 }}>
          <Grid item sx={{ width: absence.justificationFile ? '60%' : '100%', pl: '0 !important' }}>
            <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarIcon sx={{ color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="600">
                  Absence Details
                </Typography>
              </Box>
              <Stack spacing={1.5}>
                {/* Period */}
                <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between" alignItems={isEditMode ? 'flex-start' : 'center'}>
                  <Typography variant="body2" color="text.secondary">
                    Period
                  </Typography>
                  {isEditMode ? (
                    <Select
                      name="type"
                      fullWidth
                      size="small"
                      value={formData.type || ''}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          type: newType,
                          endDate: (newType === 'FULL_DAY' || newType === 'LATENESS' || newType === 'PARTIAL_DAY')
                            ? prev.date
                            : prev.endDate
                        }));
                      }}
                      sx={{ mt: isEditMode ? 0.5 : 0 }}
                    >
                      {Object.keys(AbsenceTypeLabels).map((key) => (
                        <MenuItem key={key} value={key}>
                          {AbsenceTypeLabels[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Chip
                      label={AbsenceTypeLabels[absence.type] || absence.type}
                      color={getAbsenceTypeColor(absence.type)}
                      size="small"
                    />
                  )}
                </Box>

                {/* Type */}
                <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between" alignItems={isEditMode ? 'flex-start' : 'center'}>
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  {isEditMode ? (
                    <Select
                      name="ty_pe"
                      fullWidth
                      size="small"
                      value={formData.ty_pe || ''}
                      onChange={(e) => setFormData({ ...formData, ty_pe: e.target.value })}
                      sx={{ mt: isEditMode ? 0.5 : 0 }}
                    >
                      {absenceTy_pes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Chip
                      label={ReasonTypeLabels[absence.ty_pe] || absence.ty_pe || 'Not specified'}
                      color={getReasonTypeColor(absence.ty_pe)}
                      size="small"
                    />
                  )}
                </Box>

                {/* Recorded by */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Recorded by
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {absence.recordedBy || 'Unknown'}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Date */}
                <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between" alignItems={isEditMode ? 'flex-start' : 'center'}>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={formData.date || ''}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          date: newDate,
                          endDate: (prev.type === 'FULL_DAY' || prev.type === 'LATENESS' || prev.type === 'PARTIAL_DAY')
                            ? newDate
                            : prev.endDate
                        }));
                      }}
                      sx={{ mt: isEditMode ? 0.5 : 0 }}
                    />
                  ) : (
                    <Typography variant="body2" fontWeight="500">
                      {formatDate(absence.date)}
                    </Typography>
                  )}
                </Box>

                {/* End Date */}
                {formData.type === 'MULTIPLE_DAYS' && (
                  <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between" alignItems={isEditMode ? 'flex-start' : 'center'}>
                    <Typography variant="body2" color="text.secondary">
                      End Date
                    </Typography>
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        value={formData.endDate || ''}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        sx={{ mt: isEditMode ? 0.5 : 0 }}
                      />
                    ) : (
                      <Typography variant="body2" fontWeight="500">
                        {absence.endDate ? formatDate(absence.endDate) : '...'}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Start Time (PARTIAL_DAY) */}
                {(formData.type === 'PARTIAL_DAY' && formData.ty_pe !== 'LATENESS') && (
                  <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between" alignItems={isEditMode ? 'flex-start' : 'center'}>
                    <Typography variant="body2" color="text.secondary">
                      Start Time
                    </Typography>
                    {isEditMode ? (
                      <TextField
                        size="small"
                        type="time"
                        value={formData.startTime || ''}
                        onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <TimeIcon sx={{ color: 'action.active', fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mt: isEditMode ? 0.5 : 0 }}
                      />
                    ) : (
                      <Typography variant="body2" fontWeight="500">
                        {formatTimeForDisplay(absence.startTime)}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* End Time (PARTIAL_DAY) */}
                {(formData.type === 'PARTIAL_DAY' && formData.ty_pe !== 'LATENESS') && (
                  <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between" alignItems={isEditMode ? 'flex-start' : 'center'}>
                    <Typography variant="body2" color="text.secondary">
                      End Time
                    </Typography>
                    {isEditMode ? (
                      <TextField
                        size="small"
                        type="time"
                        value={formData.endTime || ''}
                        onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <TimeIcon sx={{ color: 'action.active', fontSize: 18 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mt: isEditMode ? 0.5 : 0 }}
                      />
                    ) : (
                      <Typography variant="body2" fontWeight="500">
                        {formatTimeForDisplay(absence.endTime)}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Calculate Hours Button */}
                {isEditMode && formData.type === 'PARTIAL_DAY' && formData.ty_pe !== 'LATENESS' && (
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={calculateHoursFromTimes}
                      disabled={!formData.startTime || !formData.endTime}
                      sx={{ textTransform: 'none' }}
                    >
                      Calculate Hours
                    </Button>
                  </Box>
                )}

                {/* Duration in minutes for LATENESS */}
                {formData.ty_pe === 'LATENESS' && (
                  <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between" alignItems={isEditMode ? 'flex-start' : 'center'}>
                    <Typography variant="body2" color="text.secondary">
                      Duration (Minutes)
                    </Typography>
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={formData.minutes || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          if (!isNaN(value) && value >= 0 && value <= 120) {
                            setFormData({ ...formData, minutes: value });
                          }
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                        }}
                        inputProps={{ min: 0, max: 120 }}
                        sx={{ mt: isEditMode ? 0.5 : 0 }}
                      />
                    ) : (
                      <Typography variant="body2" fontWeight="500">
                        {absence.hours ? Math.round(absence.hours * 60) : 0} minutes
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Duration in hours for PARTIAL_DAY */}
                {formData.type === 'PARTIAL_DAY' && (
                  <Box display="flex" flexDirection={isEditMode ? 'column' : 'row'} justifyContent="space-between" alignItems={isEditMode ? 'flex-start' : 'center'}>
                    <Typography variant="body2" color="text.secondary">
                      Duration (Hours)
                    </Typography>
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={formData.hours || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value) && value >= 0 && value <= 8) {
                            setFormData({ ...formData, hours: value });
                          }
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                        }}
                        inputProps={{ min: 0, max: 8, step: 0.25 }}
                        sx={{ mt: isEditMode ? 0.5 : 0 }}
                      />
                    ) : (
                      <Typography variant="body2" fontWeight="500">
                        {absence.hours || 0} hours
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Duration */}
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {absence.endDate
                      ? `${calculateDuration(absence.date, absence.endDate)} days`
                      : (absence.type === 'LATENESS' || absence.type === 'PARTIAL_DAY')
                        ? `${Math.round(absence.hours * 60)} minutes`
                        : 'Open'}
                  </Typography>
                </Box>

                {/* Created at */}
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Submitted at
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatDate(absence.createdAt)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Justification File */}
          {absence.justificationFile && (
            <Card elevation={1} sx={{ borderRadius: 2, width: '37%', padding: 0 }}>
              <CardContent sx={{ margin: 0, padding: 0, height: '100%' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom ml="auto" mr="auto" mt={1} mb={1} textAlign="center">
                  Justification File
                </Typography>
                {absence.justificationFile.endsWith('.pdf') ? (
                  <embed src={fileUrl} type="application/pdf" width="100%" height="100%" />
                ) : (
                  <img
                    src={fileUrl}
                    alt="Justification"
                    style={{ maxWidth: '100%', maxHeight: 500, borderRadius: 8 }}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Reason for Leave */}
        <Card elevation={1} sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Reason for Leave
            </Typography>
            {isEditMode ? (
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={formData.reason || ''}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              />
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {absence.reason || "No reason specified"}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Comments */}
        {absence.comments && (
          <Card elevation={1} sx={{ mt: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Comments
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {absence.comments}
              </Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      {!IsEmployee && (
        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2, mt: 2, width: '100%' }}>
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2, mt: 2, width: '100%' }}>
              {successMessage}
            </Alert>
          )}
          {!isEditMode ? (
            <Box display="flex" gap={1} mr="auto">
              <Button
                variant="contained"
                onClick={() => setIsEditMode(true)}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Edit
              </Button>
            </Box>
          ) : (
            <Box display="flex" gap={1} mr="auto">
              <Button
                variant="contained"
                color="success"
                onClick={handleUpdate}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Update
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setIsEditMode(false);
                  setFormData({
                    ...absence,
                    minutes: Math.round(absence.hours * 60),
                    startTime: formatTimeForInput(absence.startTime),
                    endTime: formatTimeForInput(absence.endTime),
                  });
                }}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Cancel Edit
              </Button>
            </Box>
          )}
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            sx={{ borderRadius: 2 }}
            disabled={loading}
          >
            Email Employee
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2 }}
            disabled={loading}
          >
            Export Details
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AbsenceDetailsModal;
